<template>
  <div class="ms-page">
    <div class="container py-4">
      <SectionHeader label="اشتراكاتي" title="اشتراكاتي" subtitle="تابع حالة اشتراكاتك وجدّدها بسهولة" />

      <!-- Loading -->
      <div v-if="loading" class="ms-list">
        <div v-for="n in 3" :key="n" class="skel-row"></div>
      </div>

      <!-- Empty -->
      <div v-else-if="!subs.length" class="ms-empty">
        <i class="pi pi-id-card"></i>
        <p>ليس لديك أي اشتراكات بعد.</p>
        <div class="ms-empty__cta">
          <router-link to="/courses" class="btn-grad">تصفّح الكورسات</router-link>
          <!-- <router-link to="/academic-years" class="btn-ghost">السنوات الدراسية</router-link> -->
        </div>
      </div>

      <!-- List -->
      <div v-else class="ms-list">
        <div v-for="s in subs" :key="s.id" class="ms-card"
             :class="{ 'ms-card--expired': isExpiredState(s), 'ms-card--expiring': expiringSoon(s) || isExpiresToday(s) }">
          <div class="ms-card__head">
            <div>
              <h3 class="ms-card__title">{{ s.product_label || 'اشتراك' }}</h3>
              <span class="ms-card__type">{{ typeLabel(s.subscribable_type) }} · {{ planLabel(s.plan_type) }}</span>
            </div>
            <StatusBadge v-if="badge(s)" :text="badge(s).text" :tone="badge(s).tone" />
            <StatusBadge v-else :text="statusLabel(s.status)" :tone="statusTone(s.status)" />
          </div>

          <div class="ms-card__grid">
            <div><span>يبدأ</span><b>{{ date(s.starts_at) }}</b></div>
            <div><span>ينتهي</span><b>{{ isLifetime(s) ? 'مدى الحياة' : date(s.expires_at) }}</b></div>
            <div><span>المتبقي</span><b>{{ isLifetime(s) ? 'مدى الحياة' : remaining(s) }}</b></div>
          </div>

          <!-- progress bar for active subscriptions -->
          <div v-if="s.is_active && s.days_remaining !== null" class="ms-progress">
            <div class="ms-progress__bar" :style="{ width: progressPct(s) + '%' }"></div>
          </div>

          <div class="ms-card__actions">
            <button class="ms-btn ms-btn--ghost" @click="viewProduct(s)"><i class="pi pi-eye"></i> عرض</button>
            <button class="ms-btn ms-btn--ghost" @click="$router.push('/payment-history')"><i class="pi pi-history"></i> المدفوعات</button>
            <button v-if="canRenew(s)" class="ms-btn ms-btn--grad" @click="renew(s)"><i class="pi pi-refresh"></i> تجديد</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SectionHeader from '@/components/common/SectionHeader.vue';
import StatusBadge from '@/components/subscriptions/StatusBadge.vue';
import { subscriptionService } from '@/services/subscriptionService.js';
import {
  planLabel, statusLabel, statusTone, typeLabel, formatDate, daysRemainingPhrase,
  isExpiringSoon, isExpiredState, isExpiresToday, expiryStateBadge,
} from '@/composables/subscriptionMeta.js';

const TYPE_QUERY = { Course: 'course', AcademicYear: 'academic_year' };

export default {
  name: 'MySubscriptions',
  components: { SectionHeader, StatusBadge },
  data() { return { subs: [], loading: true }; },
  methods: {
    planLabel, statusLabel, statusTone, typeLabel,
    date: formatDate,
    // A lifetime (course) subscription: one-time purchase, no expiry. Robust to both
    // the explicit plan_type and any legacy active course sub with a null expiry.
    isLifetime(s) {
      const isCourse = String(s.subscribable_type || '').split('\\').pop() === 'Course';
      return s.plan_type === 'lifetime' || (isCourse && !s.expires_at && s.status === 'active');
    },
    // Driven by the backend-computed expiry_state (Subscription::expiryState()),
    // never re-derived from raw status/days_remaining here — that combination is
    // exactly what previously showed "سينتهي قريباً" for an already-expired
    // subscription whose status column hadn't been flipped by the daily cron yet.
    remaining(s) {
      if (s.expiry_state === 'expired') {
        return `انتهى في ${this.date(s.expires_at)}`;
      }
      if (s.expiry_state === 'expires_today') {
        return 'اليوم';
      }
      if (s.days_remaining === null || s.days_remaining === undefined) return '—';
      return daysRemainingPhrase(s.days_remaining);
    },
    badge: expiryStateBadge,
    expiringSoon: isExpiringSoon,
    isExpiredState,
    isExpiresToday,
    progressPct(s) {
      // Rough visual: cap the remaining-days bar at a 365-day window.
      const d = Math.max(0, Math.min(365, s.days_remaining || 0));
      return Math.round((d / 365) * 100);
    },
    canRenew(s) {
      // A lifetime course is permanent — never offer "renew".
      if (this.isLifetime(s)) return false;
      return ['expired', 'cancelled', 'active'].includes(s.status) && s.subscribable;
    },
    queryType(s) { return TYPE_QUERY[String(s.subscribable_type).split('\\').pop()] || 'course'; },
    viewProduct(s) {
      const t = this.queryType(s);
      const base = t === 'course' ? '/course' : '/academic-year';
      this.$router.push(`${base}/${s.subscribable_id}`);
    },
    renew(s) {
      this.$router.push({ path: '/subscribe', query: { type: this.queryType(s), id: s.subscribable_id, plan: s.plan_type } });
    },
    async load() {
      this.loading = true;
      try { this.subs = await subscriptionService.mySubscriptions(); }
      catch { this.subs = []; }
      finally { this.loading = false; }
    },
  },
  mounted() { this.load(); },
};
</script>

<style scoped>
.ms-page { min-height: 60vh; padding-bottom: 5rem; }
.ms-list { display: grid; gap: 1rem; }
.ms-card { background: #fff; border: 1px solid rgba(91,95,239,.1); border-radius: 16px; padding: 1.2rem; }
.ms-card--expired { border-color: rgba(239,68,68,.25); background: #fff7f7; }
.ms-card--expiring { border-color: rgba(245,158,11,.35); background: #fffaf0; }
.ms-card__head { display: flex; justify-content: space-between; align-items: flex-start; gap: .5rem; margin-bottom: 1rem; }
.ms-card__title { font-size: 1.05rem; font-weight: 800; color: #111827; margin: 0; }
.ms-card__type { font-size: .8rem; color: #6B7280; }
.ms-card__grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: .6rem; margin-bottom: 1rem; }
.ms-card__grid span { display: block; font-size: .72rem; color: #94a3b8; }
.ms-card__grid b { font-size: .88rem; color: #374151; }
.ms-progress { height: 6px; background: #eef2f7; border-radius: 99px; overflow: hidden; margin-bottom: 1rem; }
.ms-progress__bar { height: 100%; background: linear-gradient(90deg, #5B5FEF, #7c4dff); border-radius: 99px; }
.ms-card__actions { display: flex; gap: .5rem; flex-wrap: wrap; }
.ms-btn { display: inline-flex; align-items: center; gap: .35rem; border: none; border-radius: 10px; padding: .5rem .9rem; font-weight: 700; font-size: .82rem; cursor: pointer; }
.ms-btn--ghost { background: #f1f5f9; color: #374151; }
.ms-btn--grad { background: linear-gradient(90deg, #5B5FEF, #7c4dff); color: #fff; }
.ms-empty { text-align: center; padding: 3.5rem 1rem; color: #94a3b8; }
.ms-empty .pi { font-size: 2.6rem; display: block; margin-bottom: .8rem; }
.ms-empty__cta { display: flex; gap: .6rem; justify-content: center; margin-top: 1rem; }
.btn-grad { background: linear-gradient(90deg, #5B5FEF, #7c4dff); color: #fff; text-decoration: none; border-radius: 10px; padding: .55rem 1.2rem; font-weight: 700; }
.btn-ghost { color: #5B5FEF; text-decoration: none; font-weight: 700; padding: .55rem 1rem; }
.skel-row { height: 150px; border-radius: 16px; background: #e9edf5; position: relative; overflow: hidden; }
.skel-row::after { content: ''; position: absolute; inset: 0; transform: translateX(-100%); background: linear-gradient(90deg, transparent, rgba(255,255,255,.6), transparent); animation: sh 1.3s infinite; }
@keyframes sh { 100% { transform: translateX(100%); } }
@media (max-width: 480px) { .ms-card__grid { grid-template-columns: 1fr 1fr; } }
</style>
